/**
 * The single global UI store. Tiny on purpose — `useSyncExternalStore` against
 * a hand-written observable. We do NOT pull in Redux/Zustand; the surface is
 * small enough that an explicit shape is clearer than another dependency, and
 * the components that the Phase-3 swarm fills consume this via fixed hooks.
 *
 * Components NEVER mutate the store directly; they call dispatch actions
 * (`wire/actions.ts`) which forward to the socket. Server pushes (`state`,
 * `lobby`, `event`, `youAre`, `error`) flow in via `applyServer` — the single
 * inbound reducer. That keeps the binding contract one-way: server → store →
 * component. Anti-cheat by construction (ARCH §3.1): only `RedactedView` ever
 * lands in this state; the engine's secret hands never touch the client.
 */
import type {
  ErrorMsg,
  GameEventWire,
  Lobby,
  RedactedView,
  ServerMessage,
} from "@hookum/protocol";

export type ConnState =
  | { kind: "idle" }
  | { kind: "connecting" }
  | { kind: "open" }
  | { kind: "reconnecting"; attempt: number }
  /** `errorCode` (v1.4.1 Phase 21.2): when the closed state was caused by
   * a typed terminal server error that preserves the room binding (notably
   * `PROTOCOL_MISMATCH`), the original error code travels with the
   * connection state so DisconnectedPanel can render a code-specific
   * recovery affordance (e.g. "Refresh now" for PROTOCOL_MISMATCH). The
   * other terminal-error codes (BAD_CODE / ROOM_FULL / RECLAIM_FAILED /
   * GAME_IN_PROGRESS) reset the store and route back to RoomGate — they
   * never surface here. */
  | { kind: "closed"; reason: string; errorCode?: string };

export interface UiError {
  readonly code: string;
  readonly message: string;
  readonly at: number;
}

/** A bounded tail of recent events — feeds transient UI (trick sweep,
 * announcements). We never use this as a source of truth for game state;
 * that's always `view`. */
export const RECENT_EVENTS_MAX = 32;

export interface AppState {
  /** Network state. Components show banners for `reconnecting`/`closed`. */
  readonly connection: ConnState;
  /** The room code the client is talking to (null = pre-gate). */
  readonly code: string | null;
  /** Last-used display name (prefilled on the gate). */
  readonly name: string;
  /** Our seat index after `youAre`. null until bound. */
  readonly you: number | null;
  /** Issued once by `youAre`; persisted via storage.ts; used to reclaim. */
  readonly reclaimToken: string | null;
  /** Last `lobby` push from the server (drives lobby UI). */
  readonly lobby: Lobby | null;
  /** Last `state` push (the only source-of-truth for in-game UI). */
  readonly view: RedactedView | null;
  /** Bounded tail of recent events (drives animations + announcements). */
  readonly recentEvents: ReadonlyArray<{ readonly seq: number; readonly event: GameEventWire }>;
  /** Bounded tail of error messages (drives error toast UI). */
  readonly errors: ReadonlyArray<UiError>;
}

const INITIAL: AppState = {
  connection: { kind: "idle" },
  code: null,
  name: "",
  you: null,
  reclaimToken: null,
  lobby: null,
  view: null,
  recentEvents: [],
  errors: [],
};

type Listener = () => void;

export interface Store {
  /** React-friendly subscribe (returns unsub). */
  subscribe(listener: Listener): () => void;
  /** Snapshot for `useSyncExternalStore` (stable until a change). */
  getSnapshot(): AppState;
  /** REPLACE the entire state (internal use only — exposed for tests). */
  setState(next: AppState): void;
  /** Inbound reducer for every parsed `ServerMessage`. */
  applyServer(msg: ServerMessage): void;
  /** Lifecycle updates from the socket layer. */
  setConnection(c: ConnState): void;
  /** Called on first `connect()` so the store remembers what room we're in. */
  setRoom(code: string, name: string): void;
  /** Local clear (back to gate). */
  reset(): void;
}

function appendError(prev: ReadonlyArray<UiError>, e: ErrorMsg, at: number): UiError[] {
  const next = prev.concat({ code: e.code, message: e.message, at });
  return next.slice(-RECENT_EVENTS_MAX);
}

export function createStore(initial: AppState = INITIAL): Store {
  let state = initial;
  const listeners = new Set<Listener>();
  // v1.4.1 Phase 21.6 — moved inside the closure so each `createStore()`
  // gets its own counter. The default singleton (one per app load) sees no
  // behavior change, but tests that spin up multiple stores no longer share
  // module-level mutable state. Tracks event arrival order so React keys
  // stay stable through the bounded tail.
  let eventSeqCounter = 0;

  function appendEvent(
    prev: AppState["recentEvents"],
    event: GameEventWire,
  ): AppState["recentEvents"] {
    const next = prev.concat({ seq: ++eventSeqCounter, event });
    return next.slice(-RECENT_EVENTS_MAX);
  }

  function emit(): void {
    for (const l of listeners) l();
  }

  function setState(next: AppState): void {
    if (next === state) return;
    state = next;
    emit();
  }

  return {
    subscribe(l) {
      listeners.add(l);
      return () => listeners.delete(l);
    },
    getSnapshot() {
      return state;
    },
    setState,
    setConnection(c) {
      if (state.connection === c) return;
      setState({ ...state, connection: c });
    },
    setRoom(code, name) {
      setState({ ...state, code, name });
    },
    reset() {
      setState({ ...INITIAL, name: state.name });
    },
    applyServer(msg) {
      switch (msg.t) {
        case "youAre":
          setState({
            ...state,
            you: msg.seatIndex,
            reclaimToken: msg.reclaimToken,
          });
          return;
        case "lobby":
          // v1.2 Phase 14: when the server resets to lobby after a
          // game-end Continue (resetToLobby), `lobby.phase === "lobby"`
          // arrives but the old `view` (with `gameOver: true`) is still
          // in state — so the GameOver overlay would linger. Clear it
          // here so the App routes back to the Lobby screen cleanly.
          // Any subsequent `state` push from a fresh round repopulates
          // `view`.
          if (msg.lobby.phase === "lobby") {
            setState({ ...state, lobby: msg.lobby, view: null });
            return;
          }
          setState({ ...state, lobby: msg.lobby });
          return;
        case "state":
          setState({ ...state, view: msg.view });
          return;
        case "event":
          setState({
            ...state,
            recentEvents: appendEvent(state.recentEvents, msg.event),
          });
          return;
        case "error":
          setState({
            ...state,
            errors: appendError(state.errors, msg, Date.now()),
          });
          return;
      }
    },
  };
}

/** Default singleton — components import this. Tests construct their own. */
export const store: Store = createStore();
